/**
 * What not to report to Bugsnag.
 *
 * Kept apart from `lib/bugsnag.ts` so the matching can be unit-tested without
 * starting the notifier — and so the list of what we drop is a thing someone
 * can read and argue with, rather than a condition buried in a callback.
 *
 * Import-free so it tests under `node --experimental-strip-types`.
 */

/**
 * A failed `fetch()`, per browser. Every one of these is a `TypeError` thrown
 * because the request never completed at all — offline, DNS, a cancelled
 * in-flight request, CORS.
 *
 * Lowercased for comparison, and matched **exactly** rather than by substring:
 * over-reporting is the safe direction, so a message we have not seen before
 * should reach us rather than be silently dropped.
 */
export const NETWORK_FAILURE_MESSAGES = [
  "failed to fetch", // Chrome, Edge
  "load failed", // Safari
  "networkerror when attempting to fetch resource.", // Firefox
  "the network connection was lost.", // iOS WebKit
  "cancelled", // iOS WebKit, request aborted
];

export interface StackFrameLike {
  file?: string | null;
  method?: string | null;
}

/**
 * Does this stack point anywhere we could actually look?
 *
 * A browser network failure arrives with no frames, or one synthetic frame
 * reading `unknown file` / `unknown method` — there is no code location to
 * chase even in principle.
 */
export function hasUsableStack(frames?: StackFrameLike[] | null): boolean {
  if (!frames || frames.length === 0) return false;
  return frames.some((frame) => {
    const file = (frame?.file || "").trim().toLowerCase();
    return file !== "" && file !== "unknown file";
  });
}

/**
 * True for a network failure we can neither locate nor act on.
 *
 * ⚠️ **Both conditions matter.** A `Failed to fetch` that *does* carry a stack
 * is still reported, because then we know which call site it came from and can
 * decide whether it means something. It is only the stackless ones — the ones
 * that tell us nothing beyond "a request did not complete" — that are dropped.
 *
 * The case this exists for: a laptop sleeping with the app open. Firestore's
 * long-poll `fetch` is in flight, the machine suspends, the request rejects on
 * wake, and Firestore reconnects on its own with no user ever noticing. That
 * produced ~3,600 reports across 15 users with no stack trace on any of them.
 *
 * The accepted cost: if the API became unreachable at the DNS or CORS level we
 * would not learn it from here. Uptime monitoring is the right instrument for
 * that; a client error report is not.
 */
export function isUnactionableNetworkError(
  errorClass: string,
  errorMessage: string,
  frames?: StackFrameLike[] | null
): boolean {
  if (errorClass !== "TypeError") return false;
  const message = (errorMessage || "").trim().toLowerCase();
  if (!NETWORK_FAILURE_MESSAGES.includes(message)) return false;
  return !hasUsableStack(frames);
}
